import * as THREE from 'three';
import { ORDER } from '../core/systems.js';
import { KIND, KIND_NAME } from '../core/entities.js';
import {
  RIG, makeRig, createSkeleton, applyRestPose, BONE_INDEX, BONE_NAMES,
  BONE_PARENT, BONE_COUNT, PROPORTIONS, SIDE_SIGN, CHAIN, REF_HEIGHT,
} from './rig.js';
import { buildCharacterMaterials, UVRECT, FACTIONS } from './materials.js';
import { buildBody, LOD_SETTINGS } from './body.js';
import { makeSkinnedMesh } from './skin.js';
import {
  buildWeaponGeometries, makeWeapon, attach as attachTo, SOCKETS as WEAPON_SOCKETS, WEAPON_NAMES,
} from './weapons.js';
import { specFor, flatSilhouette, GEO_VARIANTS, TINT_COUNT, WEAPONS_FOR, VARIANT_COUNT, variantCountFor, variantTint, tintIndexFor } from './variants.js';
import { triCount } from './geom.js';

/**
 * Character art system — publishes `world.chars`.
 *
 * ---------------------------------------------------------------------------
 * PUBLIC API (other modules depend on this; extend, never break)
 * ---------------------------------------------------------------------------
 *   chars.rig                     the shared rig module (bone names/indices,
 *                                 makeRig, createSkeleton, applyRestPose…)
 *   chars.skeletonFor(kind)       -> { skeleton, bones, root, byName, rig }
 *                                 a fresh THREE.Skeleton for that kind's
 *                                 proportions, in bind pose.
 *   chars.meshFor(kind, seed, o)  -> THREE.SkinnedMesh, fully dressed, with its
 *                                 weapon attached (o.weapon:false to skip,
 *                                 o.team to pick the faction material,
 *                                 o.lod 0..2).
 *   chars.lods[kind]              -> [geoLOD0, geoLOD1, geoLOD2, geoLOD3flat]
 *                                 shared, faction-independent buffers for the
 *                                 crowd renderer to instance.
 *   chars.variantGeo(kind, gi, l) -> a specific geometry variant (gi < chars.geoVariants[kind])
 *   chars.materials               { sui, kai, wei, suiFlat, … , forTeam(t) }
 *   chars.weapons                 { geo, socket, make(name, team), names }
 *   chars.attach(mesh, bone, obj, socket?)
 *   chars.player                  the hero SkinnedMesh currently in the scene
 *   chars.variantCount            distinct addressable grunt appearances
 *   chars.specFor(kind, seed)     the build spec, for anyone needing metadata
 *   chars.spawnDisplay(...)       parade/showcase helper used by captures
 * ---------------------------------------------------------------------------
 */
export function createCharacters() {
  /** @type {ReturnType<typeof buildCharacterMaterials>} */
  let mats = null;
  let weaponGeo = null;
  let player = null;
  let playerBones = null;
  const geoCache = new Map();          // `${kind}:${gi}:${lod}` -> BufferGeometry
  const display = [];                  // showcase meshes (captures only)
  const KINDS = ['hero', 'grunt', 'archer', 'sergeant', 'officer', 'general'];
  let api = null;
  let t = 0;

  function key(kind, gi, lod, ti) { return kind + ':' + gi + ':' + lod + ':' + (ti | 0); }

  /**
   * `ti` selects the baked colourway. The crowd instances colourway 0 and
   * multiplies by `variantTint(seed)` per instance; `meshFor()` bakes the real
   * colourway so an individually drawn warrior is genuinely unique.
   */
  function geoFor(kind, gi = 0, lod = 0, ti = 0) {
    const k = key(kind, gi, lod, ti);
    let g = geoCache.get(k);
    if (g) return g;
    const S = specFor(kind, gi + ti * 16);
    if (lod >= 3) {
      g = flatSilhouette(S, UVRECT);
    } else {
      const r = buildBody(S, lod, UVRECT);
      g = r.geometry;
      g.userData.rigHeight = r.rig.height;
    }
    g.userData.triangles = triCount(g);
    g.userData.kind = kind; g.userData.geoVariant = gi; g.userData.lod = lod; g.userData.tint = ti;
    // pad the bounding sphere so animated limbs never pop out of frustum
    if (g.boundingSphere) g.boundingSphere.radius *= 1.35;
    geoCache.set(k, g);
    return g;
  }

  function skeletonFor(kind) {
    const S = specFor(kind, 0);
    return createSkeleton(makeRig(S));
  }

  function meshFor(kind, seed = 0, o = {}) {
    const K = PROPORTIONS[kind] ? kind : 'grunt';
    const gv = GEO_VARIANTS[K] || 1;
    const gi = ((seed % gv) + gv) % gv;
    const lod = o.lod ?? 0;
    const S = specFor(K, seed);
    const rig = makeRig(S);
    const mat = o.material || mats.forTeam(o.team ?? 0);
    const geo = geoFor(K, gi, lod, tintIndexFor(seed));
    // Per-unit standing stance so a rank of warriors is not one pose repeated.
    const stance = o.stance ?? ((seed * 2246822519) >>> 5);
    const mesh = makeSkinnedMesh(geo, rig, mat, { rest: o.rest, phase: o.phase ?? 0, stance });
    mesh.name = 'char_' + K + '_' + seed;
    mesh.userData.spec = S;
    mesh.userData.kind = K;
    if (o.weapon !== false && S.weapon) {
      const wm = makeWeapon(S.weapon, weaponGeo, mat);
      if (wm) {
        const sock = WEAPON_SOCKETS[S.weapon] || WEAPON_SOCKETS.dao;
        attachTo(mesh, sock.bone, wm, sock);
        // per-unit carry-angle jitter: a rank of identically angled spears
        // reads as a picket fence, which is the classic crowd tell
        const j = ((seed * 2654435761) >>> 0) / 4294967296;
        wm.rotation.x += (j - 0.5) * 0.34;
        wm.rotation.z += (((seed * 40503) & 255) / 255 - 0.5) * 0.30;
        wm.scale.setScalar((rig.height / REF_HEIGHT) * (sock.scale ?? 1));
        mesh.userData.weapon = wm;
        if (S.weapon === 'axe') {
          const w2 = makeWeapon('axe', weaponGeo, mat);
          attachTo(mesh, 'handL', w2, WEAPON_SOCKETS.axeL);
          w2.scale.setScalar((rig.height / REF_HEIGHT) * (WEAPON_SOCKETS.axeL.scale ?? 1));
          mesh.userData.weaponL = w2;
        }
      }
    }
    return mesh;
  }

  /**
   * Swap the steel in the hero's hands.
   *
   * The hero mesh is built ONCE at init from `specFor('hero')`, whose weapon
   * is always the great glaive. Warrior select lets the player pick a moveset,
   * and a moveset the hero is not visibly holding is the single most obvious
   * lie the front end could tell — you choose Twin Sabres, the card says Twin
   * Sabres, and the man on screen is still carrying a polearm.
   *
   * `offhand` attaches a second, mirrored copy to the left fist (twin sabres;
   * `meshFor` already does the same thing for the crowd's axe-men). Returns
   * the name actually equipped, or null if the geometry does not exist.
   */
  function setPlayerWeapon(name, o = {}) {
    if (!player || !weaponGeo || !weaponGeo[name]) return null;
    const mat = o.material || player.material;
    const drop = (obj) => { if (obj && obj.parent) obj.parent.remove(obj); };
    drop(player.userData.weapon);
    drop(player.userData.weaponL);
    player.userData.weapon = null;
    player.userData.weaponL = null;

    const h = player.userData.spec?.height || REF_HEIGHT;
    const put = (geoName, sock) => {
      const wm = makeWeapon(geoName, weaponGeo, mat);
      if (!wm) return null;
      attachTo(player, sock.bone, wm, sock);
      // No per-unit carry jitter here: `meshFor` scatters the crowd's weapons
      // so a rank does not read as a picket fence, but the hero is a single
      // silhouette the select screen orbits — a cocked blade just looks wrong.
      wm.scale.setScalar((h / REF_HEIGHT) * (sock.scale ?? 1));
      return wm;
    };

    player.userData.weapon = put(name, WEAPON_SOCKETS[name] || WEAPON_SOCKETS.dao);
    const off = o.offhand;
    if (off && weaponGeo[off]) {
      const sock = WEAPON_SOCKETS[off + 'L'] || WEAPON_SOCKETS.axeL;
      player.userData.weaponL = put(off, sock);
    }
    if (api) { api.playerWeapon = name; api.playerOffhand = off || null; }
    return name;
  }

  return {
    name: 'chars', order: ORDER.WORLD,

    init(w) {
      const t0 = performance.now();
      mats = buildCharacterMaterials(w.renderer, (w.rng?._s ?? 20240819) >>> 0);
      const tMat = performance.now();
      weaponGeo = buildWeaponGeometries(UVRECT, 1);
      const tWpn = performance.now();

      // Eagerly build the canonical LOD chain each kind exposes to the crowd.
      const lods = {};
      for (const k of KINDS) lods[k] = [geoFor(k, 0, 0), geoFor(k, 0, 1), geoFor(k, 0, 2), geoFor(k, 0, 3)];
      lods.player = lods.hero;
      const tGeo = performance.now();

      // The hero in the scene.
      player = meshFor('hero', 0, { team: 0 });
      player.name = 'playerMesh';
      playerBones = player.skeleton.bones;
      w.scene.add(player);

      const tris = {};
      for (const k of KINDS) tris[k] = lods[k].map(g => g.userData.triangles | 0);

      api = {
        /* ---- rig ---- */
        rig: {
          RIG, makeRig, createSkeleton, applyRestPose, BONE_INDEX, BONE_NAMES,
          BONE_PARENT, BONE_COUNT, SIDE_SIGN, CHAIN, PROPORTIONS, REF_HEIGHT,
        },
        skeletonFor,
        boneIndex: BONE_INDEX,
        boneNames: BONE_NAMES,
        boneParent: BONE_PARENT,
        boneCount: BONE_COUNT,

        /* ---- meshes + geometry ---- */
        meshFor,
        lods,
        variantGeo: geoFor,
        geoVariants: GEO_VARIANTS,
        tintCount: TINT_COUNT,
        weaponsFor: WEAPONS_FOR,
        specFor,
        kinds: KINDS,
        triangles: tris,

        /* ---- materials ---- */
        materials: mats.materials,
        textures: mats.textures,
        uv: UVRECT,
        factions: FACTIONS,
        materialForTeam: (team) => mats.forTeam(team),
        flatMaterialForTeam: (team) => mats.flatForTeam(team),

        /* ---- weapons ---- */
        weapons: {
          geo: weaponGeo,
          socket: WEAPON_SOCKETS,
          names: WEAPON_NAMES,
          make: (name, team = 0) => makeWeapon(name, weaponGeo, mats.forTeam(team)),
        },
        attach: (mesh, boneName, obj, socket) => attachTo(mesh, boneName, obj, socket || WEAPON_SOCKETS[obj?.name?.replace('weapon_', '')]),

        /* ---- the hero currently in the scene ---- */
        player,
        /** Capture-only: take the hero out of the draw. See lateUpdate(). */
        hidePlayer: false,
        playerBones,
        /** Re-arm the hero. See setPlayerWeapon() above. */
        setPlayerWeapon,
        playerWeapon: player.userData.spec?.weapon || 'greatGlaive',
        playerOffhand: null,

        /* ---- variants ---- */
        variantCount: VARIANT_COUNT,
        variantCountFor,
        /** per-instance colour multiplier for a crowd InstancedMesh.instanceColor */
        variantTint,
        tintIndexFor,
        /** how a seed decomposes — documented for the crowd renderer */
        addressing: { geo: 'seed % geoVariants[kind]', tint: '(seed>>4) % tintCount', weapon: '(seed>>9) % weaponsFor[kind].length' },

        /* ---- legacy shim: used to return capsule people ---- */
        humanoid(color, scale = 1) {
          const m = new THREE.Mesh(geoFor('grunt', 0, 1), mats.materials.wei);
          m.scale.setScalar(scale);
          m.castShadow = m.receiveShadow = true;
          return m;
        },

        /* ---- showcase (capture scenarios) ---- */
        display,
        spawnDisplay(kind, team, x, y, z, seed = 0, opts = {}) {
          const m = meshFor(kind, seed, { team, lod: opts.lod ?? 0, stance: opts.stance, phase: opts.phase });
          m.position.set(x, y, z);
          m.rotation.y = opts.yaw ?? 0;
          applyRestPose(m.skeleton.bones, opts.rest ?? 1, opts.phase || 0,
            opts.stance ?? ((seed * 2246822519) >>> 5));
          w.scene.add(m);
          display.push(m);
          return m;
        },
        clearDisplay() {
          for (const m of display) { m.parent?.remove(m); }
          display.length = 0;
        },
        stats: {
          buildMs: +(performance.now() - t0).toFixed(1),
          materialMs: +(tMat - t0).toFixed(1),
          weaponMs: +(tWpn - tMat).toFixed(1),
          geoMs: +(tGeo - tWpn).toFixed(1),
        },
      };
      w.chars = api;
      if (typeof console !== 'undefined') {
        console.log('[chars] built in', api.stats.buildMs, 'ms', JSON.stringify(tris));
      }
    },

    update(w, dt, alpha, realDt) {
      t += realDt || dt;
      // Until the animation system takes over, keep the hero breathing so he
      // never reads as a mannequin.
      if (!w.anim?.ready && playerBones) {
        applyRestPose(playerBones, 1, t * 1.6);
        const b = playerBones;
        const sway = Math.sin(t * 0.9) * 0.02;
        b[BONE_INDEX.spine1].rotation.y = sway * 0.5;
        b[BONE_INDEX.chest].rotation.y = -sway;
        b[BONE_INDEX.head].rotation.y = sway * 1.4;
        b[BONE_INDEX.root].position.y = Math.sin(t * 1.6) * 0.008;
      }
    },

    lateUpdate(w) {
      const e = w.entities, id = w.playerId;
      if (id < 0 || !player) return;
      player.position.set(e.x[id], e.y[id], e.z[id]);
      player.rotation.y = e.yaw[id];
      // `hidePlayer` is for close-read capture scenarios that need the ground,
      // a prop or a wall with nothing standing on the boom in front of it.
      // Without it a scenario cannot take the hero out of shot at all: this
      // line runs once per frame before the draw, so a scenario setting
      // `chars.player.visible = false` is overwritten before anything is
      // rendered — which is why world_props_r5 and props_grass both asked for
      // it and both photographed his armour anyway.
      player.visible = e.alive[id] !== 0 && !api.hidePlayer;
    },

    dispose() {
      mats?.dispose();
      for (const g of geoCache.values()) g.dispose();
      geoCache.clear();
      if (weaponGeo) for (const k in weaponGeo) weaponGeo[k].dispose();
    },
  };
}
